import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const RANGES = [['week', 'This week', 7], ['two', 'Last 2 weeks', 14], ['month', 'Last 4 weeks', 28], ['quarter', 'Last 12 weeks', 84]];
const fmt = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function CheckCard({ c }) {
  const [open, setOpen] = useState(false);
  const id = c.id;
  return (
    <Card className="p-3" data-testid={`stock-check-${id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left flex items-start gap-3" data-testid={`stock-check-toggle-${id}`}>
        <div className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${c.adjustments_count ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {c.adjustments_count ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-gray-900" data-testid={`stock-check-who-${id}`}>{c.performed_by_name || 'Unknown'} <span className="font-normal text-gray-500">· {c.brand_label}</span></div>
          <div className="text-xs text-gray-600 mt-0.5" data-testid={`stock-check-when-${id}`}>{fmt(c.created_at)}{c.source === 'rebuilt' ? ' · rebuilt from count log' : ''}</div>
          <div className="text-xs mt-1 flex flex-wrap gap-2">
            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-semibold">{c.confirmed_count} confirmed</span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${c.adjustments_count ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>{c.adjustments_count} adjusted</span>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t grid md:grid-cols-2 gap-3 text-xs" data-testid={`stock-check-detail-${id}`}>
          <div>
            <div className="font-bold text-amber-800 mb-1">Discrepancies</div>
            {c.adjustments?.length ? c.adjustments.map((a, i) => (
              <div key={i} className="py-1 border-b last:border-b-0">
                <div className="font-semibold text-gray-900">{a.name}</div>
                <div className="text-gray-600">Expected {a.expected_qty} → counted {a.actual_qty} ({a.delta > 0 ? '+' : ''}{a.delta}) · {a.reason || 'no reason given'}</div>
              </div>
            )) : <p className="text-gray-500">None — all counts matched.</p>}
          </div>
          <div>
            <div className="font-bold text-emerald-800 mb-1">Confirmed as correct</div>
            {c.confirmed?.length ? <ul className="columns-1 sm:columns-2 gap-3">{c.confirmed.map((p, i) => <li key={i} className="text-gray-700 break-inside-avoid">{p.name}{p.qty != null ? <span className="text-gray-400"> · {p.qty}</span> : null}</li>)}</ul> : <p className="text-gray-500">None.</p>}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function StockCheckHistory() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [range, setRange] = useState('week');
  const [checks, setChecks] = useState(null);
  const [error, setError] = useState('');
  const days = RANGES.find((r) => r[0] === range)[2];
  useEffect(() => {
    if (!user) return;
    setChecks(null); setError('');
    fetch(`${API_URL}/api/stock/checks?days=${days}`, { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not load'); return r.json(); })
      .then((d) => setChecks(d.checks || []))
      .catch((e) => { setError(e.message); setChecks([]); });
  }, [user, days]);
  const people = useMemo(() => {
    const m = {};
    (checks || []).forEach((c) => { m[c.performed_by_name || 'Unknown'] = (m[c.performed_by_name || 'Unknown'] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [checks]);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-3">
          <button onClick={() => navigate('/staff/stock/check')} data-testid="sch-back" className="p-2 hover:bg-white/10 rounded-lg" aria-label="Back to Stock Check"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2"><ClipboardCheck className="w-6 h-6" style={{ color: '#D4A24B' }} /> Stock Check History</h1>
            <p className="text-white/70 text-sm">Who checked what, when — and anything they adjusted</p>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="sch-range">
          {RANGES.map(([k, l]) => <Button key={k} size="sm" variant={range === k ? 'default' : 'outline'} onClick={() => setRange(k)} className={range === k ? 'bg-[#1f4030] text-white' : 'bg-white'} data-testid={`sch-range-${k}`}>{l}</Button>)}
        </div>
        {checks && checks.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="sch-people">
            {people.map(([n, k]) => <span key={n} className="px-2.5 py-1 rounded-full bg-white border font-semibold text-gray-800">{n} <span className="text-gray-500 font-normal">· {k} check{k === 1 ? '' : 's'}</span></span>)}
          </div>
        )}
        {error && <p className="text-sm text-rose-700" data-testid="sch-error">{error}</p>}
        {checks === null && <p className="text-sm text-gray-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
        {checks && checks.length === 0 && !error && <Card className="p-6 text-center text-sm text-gray-500" data-testid="sch-empty">No stock checks in this period.</Card>}
        <div className="space-y-2" data-testid="sch-list">{(checks || []).map((c) => <CheckCard key={c.id} c={c} />)}</div>
      </div>
    </div>
  );
}
